<template>
  <div>
    <div>
      <div class="d-flex align-items-center">
        <div>
          <h1 class="page-title">
            <a-icon type="arrow-left" @click="handleCancelOrder" />
            {{ $t('manualOrder.create_title') }}
          </h1>
        </div>
      </div>
    </div>
    <a-form-model
      ref="ruleForm"
      :model="form"
      :rules="rules"
      :class="{ info: isDetail }"
      @submit="onSubmit"
      @submit.native.prevent
    >
      <a-row :gutter="16" type="flex" align="top">
        <a-col :xs="24" :lg="16">
          <a-card :title="$t('manualOrder.transaction_detail')" class="w-100 mt-2 shadow" :bordered="false">
            <a-row :gutter="[16, 8]">
              <a-col :xs="24" :md="12">
                <a-form-model-item :label="$t('manualOrder.order_number')" prop="orderNumber">
                  <a-input
                    v-model="form.orderNumber"
                    size="large"
                    autocomplete="off"
                    :placeholder="$t('manualOrder.input_order_number')"
                    allow-clear
                    style="text-transform: uppercase"
                    @keyup="e => e.target.value = e.target.value.toUpperCase()"
                    @blur="checkOrderNumber"
                  />
                </a-form-model-item>
              </a-col>
              <a-col :xs="24" :md="12">
                <a-form-model-item :label="$t('manualOrder.warehouse')" prop="warehouseId">
                  <a-select
                    v-model="form.warehouseId"
                    size="large"
                    class="select-antd-default"
                    show-search
                    :filter-option="false"
                    :not-found-content="warehouseSearching ? undefined : null"
                    @change="onChangeWarehouse"
                    @search="handleWarehouseSearch"
                  >
                    <a-spin v-if="warehouseSearching" slot="notFoundContent" size="small" />
                    <a-select-option
                      v-for="item in warehouseList"
                      :key="item.value"
                      :value="item.value"
                    >
                      {{ item.label }}
                    </a-select-option>
                  </a-select>
                </a-form-model-item>
              </a-col>
              <a-col :xs="24" :md="12">
                <a-form-model-item :label="$t('manualOrder.date')" prop="orderDate">
                  <a-date-picker
                    v-model="form.orderDate"
                    show-time
                    size="large"
                    class="w-100"
                    :placeholder="$t('manualOrder.input_date')"
                    :format="dateFormat"
                    :disabled-date="disabledDate"
                  />
                </a-form-model-item>
              </a-col>
              <a-col :xs="24" :md="12">
                <a-form-model-item :label="$t('manualOrder.payment_method')" prop="paymentType">
                  <a-select
                    v-model="form.paymentType"
                    size="large"
                    class="select-antd-default"
                    :placeholder="$t('manualOrder.payment_status_placeholder')"
                  >
                    <a-select-option
                      v-for="item in paymentList"
                      :key="item.method"
                      :value="item.method"
                    >
                      {{ item.payment_name }}
                    </a-select-option>
                  </a-select>
                </a-form-model-item>
              </a-col>
              <a-col :xs="24" :md="12">
                <a-form-model-item :label="$t('manualOrder.attachment_url')" prop="attachmentUrl">
                  <a-input
                    v-model="form.attachmentUrl"
                    size="large"
                    autocomplete="off"
                    :placeholder="$t('manualOrder.input_attachment_url')"
                    allow-clear
                  />
                </a-form-model-item>
              </a-col>
              <a-col :xs="24" :md="12">
                <a-form-model-item :label="$t('manualOrder.upload_attachment')">
                  <a-upload
                    list-type="picture"
                    :file-list="fileList"
                    :multiple="true"
                    :remove="handleRemove"
                    :before-upload="beforeUpload"
                    accept=".pdf"
                  >
                    <a-button type="primary" ghost size="large" class="w-100">
                      <a-icon type="upload" /> {{ $t('manualOrder.upload_attachment') }}
                    </a-button>
                  </a-upload>
                </a-form-model-item>
              </a-col>
            </a-row>
          </a-card>

          <a-card :title="$t('manualOrder.buyer_data')" class="w-100 mt-2 shadow" :bordered="false">
            <a-row :gutter="[16, 8]">
              <a-col :xs="24" :sm="12">
                <a-form-model-item :label="$t('manualOrder.first_name')" prop="firstName">
                  <a-input
                    v-model="form.firstName"
                    size="large"
                    :max-length="25"
                    autocomplete="off"
                    :placeholder="$t('manualOrder.input_first_name')"
                    allow-clear
                  />
                </a-form-model-item>
              </a-col>
              <a-col :xs="24" :sm="12">
                <a-form-model-item :label="$t('manualOrder.last_name')" prop="lastName">
                  <a-input
                    v-model="form.lastName"
                    size="large"
                    :max-length="25"
                    autocomplete="off"
                    :placeholder="$t('manualOrder.input_last_name')"
                    allow-clear
                  />
                </a-form-model-item>
              </a-col>
              <a-col :xs="24" :sm="12">
                <a-form-model-item :label="$t('customer.email')" prop="email">
                  <a-input
                    v-model="form.email"
                    size="large"
                    :max-length="50"
                    autocomplete="off"
                    :placeholder="$t('manualOrder.input_email')"
                    allow-clear
                  />
                </a-form-model-item>
              </a-col>
              <a-col :xs="24" :sm="12">
                <a-form-model-item :label="$t('customer.mobile')" prop="phoneNumber">
                  <a-input
                    v-model="form.phoneNumber"
                    class="select-input"
                    size="large"
                    :max-length="25"
                    autocomplete="off"
                    :placeholder="$t('manualOrder.input_phone')"
                    allow-clear
                  >
                    <a-select
                      slot="addonBefore"
                      v-model="form.extPhone"
                      option-label-prop="children"
                      class="select-phone-code"
                    >
                      <a-select-option
                        v-for="(item, index) in codeCountry"
                        :key="index"
                        :value="item.value"
                        :label="item.value"
                        class="d-flex"
                      >
                        <keep-alive>
                          <component :is="flagComponent(item.value)" />
                        </keep-alive>
                        <p style="margin-left: 10px">
                          {{ item.label }}
                        </p>
                      </a-select-option>
                    </a-select>
                  </a-input>
                </a-form-model-item>
              </a-col>
            </a-row>
          </a-card>

          <a-card :title="$t('manualOrder.shipping_data')" class="w-100 mt-2 shadow" :bordered="false">
            <a-spin size="large" :spinning="calculatingShippingCost" :indicator="customIndicatorCalculatingShippingCost">
              <a-form-model-item
                :label="$t('manualOrder.shipping_type')"
                prop="withCourier"
                class="mb-3"
              >
                <a-radio-group
                  v-model.trim="form.withCourier"
                  name="withCourier"
                  class="radio-input"
                >
                  <a-radio class="text-note" :value="true">
                    {{ $t('manualOrder.courier') }}
                  </a-radio>
                  <a-radio class="text-note" :value="false">
                    {{ $t('manualOrder.self_pickup') }}
                  </a-radio>
                </a-radio-group>
              </a-form-model-item>
              
              <a-row v-if="form.withCourier" :gutter="[16, 8]">
                <a-col :xs="24" :sm="12">
                  <a-form-model-item
                    :label="$t('distributor.country')"
                    prop="country"
                  >
                    <a-spin :spinning="loadingCountries">
                      <a-select
                        v-model="form.country"
                        class="select-antd-default"
                        size="large"
                        show-arrow
                        show-search
                        :filter-option="filterOptionCity"
                        :placeholder="`${$t('utils.select')} ${$t('distributor.country')}`"
                        @change="onChangeCountry(false)"
                      >
                        <a-select-option v-for="country in countries" :key="country.value" :value="country.value" :title="country.label">
                          {{ country.label }}
                        </a-select-option>
                      </a-select>
                    </a-spin>
                  </a-form-model-item>
                </a-col>

                <a-col :xs="24" :sm="12">
                  <a-form-model-item
                    :label="$t('distributor.city')"
                    prop="city"
                  >
                    <a-input
                      v-if="form.country != '228'"
                      v-model="form.city"
                      size="large"
                      :placeholder="$t('distributor.city')"
                    />
                    <a-select
                      v-else
                      v-model="form.city"
                      class="select-antd-default"
                      size="large"
                      show-arrow
                      show-search
                      :filter-option="filterOptionCity"
                      :not-found-content="loadingCities ? undefined : null"
                      :disabled="!(!!form.country)"
                      :placeholder="`${$t('utils.select')} ${$t('distributor.city')}`"
                      @change="onChangeCity(false)"
                      @focus="getCities(false)"
                    >
                      <a-spin v-if="loadingCities" slot="notFoundContent" size="small" />
                      <a-select-option v-for="city in cities" :key="city.value" :value="city.value" :title="city.label">
                        {{ city.label }}
                      </a-select-option>
                    </a-select>
                  </a-form-model-item>
                </a-col>

                <a-col :xs="24" :sm="12">
                  <a-form-model-item
                    :label="form.country != '228' ? $t('dashboardContent.province') : $t('distributor.subdistrict')"
                    prop="subdistrict"
                  >
                    <a-input
                      v-if="form.country != '228'"
                      v-model="form.subdistrict"
                      size="large"
                      :placeholder="$t('dashboardContent.province')"
                    />
                    <a-select
                      v-else
                      v-model="form.subdistrict"
                      class="select-antd-default"
                      size="large"
                      show-arrow
                      show-search
                      :disabled="!(!!form.city)"
                      :filter-option="filterOptionCity"
                      :not-found-content="loadingSubdistrict ? undefined : null"
                      :placeholder="`${$t('utils.select')} ${$t('distributor.subdistrict')}`"
                      @change="onChangeDistrict(false)"
                      @focus="getSubdistrict"
                    >
                      <a-select-option
                        v-if="!form.city?.length"
                        key="novalid"
                        value=""
                        disabled
                        class="text-dark"
                      >
                        {{ $t('distributor.choose_city') }}
                      </a-select-option>
                      <a-spin v-else-if="loadingSubdistrict && form.city?.length" slot="notFoundContent" size="small" />
                      <a-select-option v-for="sub in subdistricts" :key="sub.value" :value="sub.value" :title="sub.label">
                        {{ sub.label }}
                      </a-select-option>
                    </a-select>
                  </a-form-model-item>
                </a-col>

                <a-col :xs="24" :sm="12">
                  <a-form-model-item
                    :label="$t('distributor.postal_code')"
                    prop="postcode"
                  >
                    <a-input
                      v-model.trim="form.postcode"
                      size="large"
                      :placeholder="$t('distributor.postal_code')"
                    />
                  </a-form-model-item>
                </a-col>

                <a-col :xs="24">
                  <a-form-model-item
                    :label="$t('manualOrder.street_name')"
                    prop="addressLine1"
                  >
                    <a-textarea
                      v-model="form.addressLine1"
                      size="large"
                      :auto-size="{ minRows: 2, maxRows: 9 }"
                      :placeholder="$t('manualOrder.street_name')"
                    />
                  </a-form-model-item>
                </a-col>
              </a-row>

              <a-divider v-if="form.withCourier" class="my-3" />

              <a-spin v-if="form.withCourier" :indicator="customIndicatorFormCourier" :spinning="!form.subdistrict || !form.warehouseId">
                <a-form-model-item
                  :label="$t('manualOrder.courier_type')"
                  prop="isIntegratedCourier"
                  class="mb-3"
                >
                  <a-radio-group
                    v-model.trim="form.isIntegratedCourier"
                    name="isIntegratedCourier"
                    class="radio-input"
                  >
                    <a-tooltip placement="bottom">
                      <template #title>
                        {{ $t('manualOrder.custom_courier_tooltip') }}
                      </template>
                      <a-radio class="text-note" :value="false">
                        {{ $t('manualOrder.custom_courier') }}
                      </a-radio>
                    </a-tooltip>
                    <a-tooltip v-if="findMetaName('manual_order_integrated_courier')?.includes($route.params.id)" placement="bottom">
                      <template #title>
                        {{ $t('manualOrder.integrated_courier_tooltip') }}
                      </template>
                      <a-radio class="text-note" :value="true">
                        {{ $t('manualOrder.integrated_courier') }}
                      </a-radio>
                    </a-tooltip>
                  </a-radio-group>
                </a-form-model-item>

                <template v-if="!form.isIntegratedCourier">
                  <a-row :gutter="[16, 8]">
                    <a-col :xs="24" :sm="12">
                      <a-form-model-item
                        :label="$t('manualOrder.courier')"
                        prop="courierName"
                      >
                        <a-select
                          v-model="form.courierName"
                          class="select-antd-default"
                          size="large"
                          show-arrow
                          show-search
                          :placeholder="$t('manualOrder.choose_courier')"
                          :filter-option="false"
                          :loading="loadingScrollCouriers"
                          :not-found-content="loadingScrollCouriers ? 'Loading...' : 'No data found'"
                          @popupScroll="handleScrollCouriers"
                          @search="searchOptionCourier"
                        >
                          <a-select-option v-for="itemCourier in displayedCouriers" :key="itemCourier.id" :value="itemCourier.id" :title="itemCourier.courier_name">
                            {{ itemCourier.courier_name }}
                          </a-select-option>
                        </a-select>
                      </a-form-model-item>
                    </a-col>

                    <a-col :xs="24" :sm="12">
                      <a-form-model-item
                        :label="$t('manualOrder.courier_service')"
                        prop="courierService"
                      >
                        <a-select
                          v-model="form.courierService"
                          :disabled="!form.courierName"
                          :filter-option="filterOptionCity"
                          class="select-antd-default"
                          size="large"
                          show-arrow
                          show-search
                          :placeholder="$t('manualOrder.choose_service')"
                          @focus="fetchShippingService"
                        >
                          <a-select-option v-for="(itemService, index) in courierServices" :key="index" :value="index" :title="itemService.service_name">
                            {{ [itemService?.service_name, itemService?.service_type].filter(item => !!item && item !== '').join(' - ') }}
                          </a-select-option>
                        </a-select>
                      </a-form-model-item>
                    </a-col>

                    <a-col :xs="24" :sm="12">
                      <a-form-model-item label="Air Way Bill No." prop="awbNumber">
                        <a-input
                          v-model="form.awbNumber"
                          :max-length="25"
                          size="large"
                          autocomplete="off"
                          :placeholder="$t('manualOrder.input_awb')"
                        />
                      </a-form-model-item>
                    </a-col>
                  </a-row>
                </template>
                <template v-else>
                  <a-row :gutter="[16, 8]">
                    <a-col :xs="24">
                      <a-form-model-item
                        :label="$t('manualOrder.courier_service')"
                        prop="courierIntegrated"
                      >
                        <a-select
                          v-model="form.courierIntegrated"
                          :filter-option="filterOptionCity"
                          class="select-antd-default"
                          size="large"
                          show-arrow
                          show-search
                          :placeholder="$t('manualOrder.choose_service')"
                          :not-found-content="fetchingCourierIntegrated ? undefined : null"
                          @focus="fetchShippingDataLogapi"
                        >
                          <a-spin v-if="fetchingCourierIntegrated" slot="notFoundContent" size="small" />
                          <template v-else>
                            <a-select-option v-if="!listCourierIntegrated.length" class="text-dark" disabled value="not-found">
                              {{ $t('manualOrder.courier_not_available') }}
                            </a-select-option>
                            <a-select-option v-for="(item, index) in listCourierIntegrated" :key="index" :value="index">
                              {{ [item.name, item.service?.name, `${currency} ${toCurrency(item.price) }`].filter(item => !!item && item !== '').join(' - ') }}
                            </a-select-option>
                          </template>
                        </a-select>
                      </a-form-model-item>
                    </a-col>
                  </a-row>
                </template>

                <a-row :gutter="[16, 8]" class="mt-2">
                  <a-col :xs="24" :sm="12">
                    <a-form-model-item
                      label=""
                      prop="withLabelUrl"
                      class="mb-0"
                    >
                      <a-radio-group
                        v-model.trim="form.withLabelUrl"
                        name="withLabelUrl"
                        class="radio-input"
                      >
                        <a-radio class="text-note" value="own">
                          {{ $t('manualOrder.gen_own_label') }}
                        </a-radio>
                        <a-radio class="text-note" value="url">
                          {{ $t('manualOrder.url') }}
                        </a-radio>
                      </a-radio-group>
                    </a-form-model-item>
                  </a-col>
                  <a-col v-if="form.withLabelUrl === 'url'" :xs="24" :sm="12">
                    <a-form-model-item
                      label=""
                      prop="shippingUrlLabel"
                      :validate-status="!isValidShippingUrlLabel ? 'error' : ''"
                    >
                      <a-input
                        v-model="form.shippingUrlLabel"
                        size="large"
                        autocomplete="off"
                        :placeholder="$t('manualOrder.input_label_url')"
                      />
                      <span>
                        <a-alert
                          v-if="!isValidShippingUrlLabel"
                          type="error"
                          class="mt-2"
                        >
                          <template #description>
                            <p v-html="$t('manualOrder.invalid_url_alert')" />
                            <p class="mb-0">{{ $t('manualOrder.valid_url_example') }}</p>
                            <ul>
                              <li>https://example.com/label</li>
                              <li>www.example.com/label</li>
                              <li>example.com/label/12345</li>
                            </ul>
                          </template>
                        </a-alert>
                      </span>
                    </a-form-model-item>
                  </a-col>
                </a-row>
              </a-spin>
            </a-spin>
          </a-card>

          <a-card :title="$t('manualOrder.billing_data')" class="w-100 mt-2 mb-4 shadow" :bordered="false">
            <a-form-model-item
              label=""
              prop="sameWithShippingAddress"
              class="mb-3"
            >
              <a-checkbox
                v-model="form.sameWithShippingAddress"
                :disabled="!form.withCourier"
                @change="scrollToBottom"
              >
                {{ $t('manualOrder.use_shipping_address') }}
              </a-checkbox>
            </a-form-model-item>

            <a-row v-if="!form.sameWithShippingAddress" :gutter="[16, 8]">
              <a-col :xs="24" :sm="12">
                <a-form-model-item
                  :label="$t('distributor.country')"
                  prop="billingCountry"
                >
                  <a-spin :spinning="loadingCountries">
                    <a-select
                      v-model="form.billingCountry"
                      class="select-antd-default"
                      size="large"
                      show-arrow
                      show-search
                      :filter-option="filterOptionCity"
                      :placeholder="$t('manualOrder.select_country')"
                      @change="onChangeCountry(true)"
                    >
                      <a-select-option v-for="country in billingCountries" :key="country.value" :value="country.value" :title="country.label">
                        {{ country.label }}
                      </a-select-option>
                    </a-select>
                  </a-spin>
                </a-form-model-item>
              </a-col>

              <a-col :xs="24" :sm="12">
                <a-form-model-item
                  :label="$t('distributor.city')"
                  prop="billingCity"
                >
                  <a-input
                    v-if="form.billingCountry != '228'"
                    v-model="form.billingCity"
                    size="large"
                    :placeholder="$t('distributor.city')"
                  />
                  <a-select
                    v-else
                    v-model="form.billingCity"
                    class="select-antd-default"
                    size="large"
                    show-arrow
                    show-search
                    :filter-option="filterOptionCity"
                    :not-found-content="loadingCities ? undefined : null"
                    :disabled="!(!!form.billingCountry)"
                    :placeholder="$t('manualOrder.select_city')"
                    @change="onChangeCity(true)"
                    @focus="getCities(true)"
                  >
                    <a-spin v-if="loadingCities" slot="notFoundContent" size="small" />
                    <a-select-option v-for="city in billingCities" :key="city.value" :value="city.value" :title="city.label">
                      {{ city.label }}
                    </a-select-option>
                  </a-select>
                </a-form-model-item>
              </a-col>

              <a-col :xs="24" :sm="12">
                <a-form-model-item
                  :label="form.billingCountry != '228' ? $t('dashboardContent.province') : $t('distributor.subdistrict')"
                  prop="billingSubdistrict"
                >
                  <a-input
                    v-if="form.billingCountry != '228'"
                    v-model="form.billingSubdistrict"
                    size="large"
                    :placeholder="$t('dashboardContent.province')"
                  />
                  <a-select
                    v-else
                    v-model="form.billingSubdistrict"
                    class="select-antd-default"
                    size="large"
                    show-arrow
                    show-search
                    :disabled="!(!!form.billingCity)"
                    :filter-option="filterOptionCity"
                    :not-found-content="loadingSubdistrict ? undefined : null"
                    :placeholder="$t('manualOrder.select_district')"
                    @change="onChangeDistrict(true)"
                    @focus="getSubdistrict(true)"
                  >
                    <a-select-option
                      v-if="!form.billingCity.length"
                      key="novalid"
                      value=""
                      disabled
                      class="text-dark"
                    >
                      {{ $t('manualOrder.please_choose_city') }}
                    </a-select-option>
                    <a-spin v-else-if="loadingSubdistrict && form.billingCity.length" slot="notFoundContent" size="small" />
                    <a-select-option v-for="sub in billingSubdistricts" :key="sub.value" :value="sub.value" :title="sub.label">
                      {{ sub.label }}
                    </a-select-option>
                  </a-select>
                </a-form-model-item>
              </a-col>

              <a-col :xs="24" :sm="12">
                <a-form-model-item
                  :label="$t('distributor.postal_code')"
                  prop="billingPostcode"
                >
                  <a-input
                    v-model.trim="form.billingPostcode"
                    size="large"
                    :placeholder="$t('distributor.postal_code')"
                  />
                </a-form-model-item>
              </a-col>

              <a-col :xs="24">
                <a-form-model-item
                  :label="$t('manualOrder.street_name')"
                  prop="billingAddressLine1"
                >
                  <a-textarea
                    v-model="form.billingAddressLine1"
                    size="large"
                    :auto-size="{ minRows: 2, maxRows: 9 }"
                    :placeholder="$t('manualOrder.street_name')"
                  />
                </a-form-model-item>
              </a-col>
            </a-row>
          </a-card>
        </a-col>

        <a-col :xs="24" :lg="8" class="sticky-col">
          <div class="sticky-sidebar">
            <div class="sidebar-scroll-area">
            <a-card :title="$t('manualOrder.cart_title')" class="w-100 mt-2 shadow" :bordered="false">
              <template #extra>
                <span class="text-muted small">{{ $t('dashboardContent.total_sku') }}: <b>{{ computedValidSKU || 0 }}</b></span>
              </template>

              <template v-if="itemList.filter(i => i.catalog_id).length > 0">
                <div class="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom">
                  <span class="text-muted extra-small font-weight-bold">{{ $t('manualOrder.product_summary') }}</span>
                  <a-button
                    type="link"
                    size="small"
                    class="p-0 text-primary font-weight-bold"
                    style="font-size: 11px;"
                    @click="isCartCollapsed = !isCartCollapsed"
                  >
                    {{ isCartCollapsed ? $t('manualOrder.show_list') : $t('manualOrder.hide_list') }}
                    <a-icon :type="isCartCollapsed ? 'down' : 'up'" class="ml-1" />
                  </a-button>
                </div>

                <div v-show="!isCartCollapsed" class="mini-cart-container mb-3">
                  <div class="mini-cart-list">
                    <div
                      v-for="(item, idx) in itemList.filter(i => i.catalog_id)"
                      :key="idx"
                      class="mini-cart-item p-2 mb-2 rounded border"
                    >
                      <div class="d-flex justify-content-between align-items-start mb-1">
                        <div class="product-title font-weight-bold text-dark flex-grow-1 pr-2">
                          {{ item.catalog_title || inventories.find(i => `${i.id}.${i.product_id}` === item.catalog_id)?.title || 'Pilih Produk...' }}
                        </div>
                        <div class="font-weight-bold text-dark text-nowrap" style="font-size: 13px; white-space: nowrap;">
                          {{ currency }} {{ toCurrency(item.total_price) }}
                        </div>
                      </div>

                      <div class="mb-2">
                        <a-tag class="text-monospace text-small">
                          {{ item.sku || inventories.find(i => `${i.id}.${i.product_id}` === item.catalog_id)?.sku || '-' }}
                        </a-tag>
                      </div>

                      <div class="d-flex align-items-end justify-content-between">
                        <div class="extra-small" style="color: #64748b; line-height: 1.6;">
                          <div>Qty: <b>{{ item.quantity || 0 }}</b></div>
                          <template v-if="item.product_discount && item.product_discount > 0">
                            <div>
                              <span style="text-decoration: line-through; color: #94a3b8;">{{ currency }} {{ toCurrency(item.selling_price) }}</span>
                              <span class="mx-1 text-muted">–</span>
                              <span style="color: #e11d48;">{{ currency }} {{ toCurrency(item.product_discount) }}</span>
                              <span class="mini-off-badge ml-1">OFF</span>
                            </div>
                            <div class="font-weight-bold" style="font-size: 12px; color: #0f172a;">
                              {{ currency }} {{ toCurrency((item.selling_price || 0) - (item.product_discount || 0)) }}
                            </div>
                          </template>
                          <template v-else>
                            <div style="font-size: 11px;">
{{ currency }} {{ toCurrency(item.selling_price) }}
</div>
                          </template>
                        </div>
                        <div class="d-flex flex-column align-items-center" style="gap: 4px; margin-left: 6px;">
                          <a-tooltip title="Edit Produk">
                            <a-button
                              type="link"
                              size="small"
                              class="p-0"
                              @click="handleOpenCartModal"
                            >
                              <a-icon type="edit" style="color: #3b82f6; font-size: 14px; cursor:pointer;" />
                            </a-button>
                          </a-tooltip>
                          <a-popconfirm
                            :title="$t('manualOrder.delete_cart_confirm')"
                            :ok-text="$t('utils.delete')"
                            :cancel-text="$t('utils.cancel')"
                            ok-type="danger"
                            placement="left"
                            @confirm="onDeleteItem(item)"
                          >
                            <a-button
                              type="link"
                              size="small"
                              class="p-0"
                            >
                              <TrashIcon style="color: #E00000; cursor:pointer; width: 14px; height: 14px;" />
                            </a-button>
                          </a-popconfirm>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-show="isCartCollapsed" class="p-2 mb-3 bg-light border rounded d-flex justify-content-between align-items-center" style="font-size: 12px;">
                  <span class="text-muted"><b>{{ computedValidSKU }}</b> {{ $t('manualOrder.product_in_cart') }}</span>
                  <a-button type="link" size="small" class="p-0" @click="handleOpenCartModal">
                    {{ $t('manualOrder.view_detail_table') }} <a-icon type="eye" />
                  </a-button>
                </div>
              </template>
              <div v-else class="text-center py-4 my-2 text-muted border rounded bg-light">
                <a-icon type="shopping-cart" style="font-size: 28px; color: #cbd5e1; display: block; margin-bottom: 8px;" />
                <div style="font-size: 12px; color: #94a3b8;">
{{ $t('manualOrder.no_product_in_cart') }}
</div>
              </div>

              <div class="d-flex" style="gap: 8px;">
                <a-button
                  type="primary"
                  ghost
                  class="flex-grow-1"
                  @click="handleOpenCartModal"
                >
                  <a-icon type="shopping-cart" /> {{ itemList.length > 0 ? $t('manualOrder.manage_edit_product') : $t('manualOrder.add_product') }}
                </a-button>
              </div>
            </a-card>

            <a-card :title="$t('manualOrder.transaction_summary')" class="w-100 mt-2 shadow" :bordered="false">
              <div class="summary-details">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <span class="text-muted" style="font-size: 13px;">{{ $t('manualOrder.sub_total_cart') }}</span>
                  <span class="font-weight-bold tabular-nums" style="font-size: 14px;">{{ `${currency} ${toCurrency(computedSubTotal)}` }}</span>
                </div>

                <div class="summary-row mb-2">
                  <span class="summary-label text-muted">{{ $t('manualOrder.discount') }}</span>
                  <div class="summary-input-wrap">
                    <a-form-model-item class="m-0" prop="totalDiscount">
                      <a-input-group compact class="summary-input-group">
                        <span class="input-addon-currency">{{ currency }}</span>
                        <a-input-number
                          v-model="form.totalDiscount"
                          class="nostep flex-grow-1"
                          size="default"
                          autocomplete="off"
                          :precision="precision"
                          :formatter="value => inputFormatter(value)"
                          :parser="value => inputParser(value)"
                          @change="onChangeDiscAndVoucherMaxValue($event, computedSubTotal - form.totalVoucher, 'totalDiscount')"
                        />
                      </a-input-group>
                    </a-form-model-item>
                  </div>
                </div>

                <div class="summary-row mb-2">
                  <span class="summary-label text-muted">{{ $t('manualOrder.shipping_cost') }}</span>
                  <div class="summary-input-wrap">
                    <a-form-model-item class="m-0" prop="shippingCost">
                      <a-input-group compact class="summary-input-group">
                        <span class="input-addon-currency">{{ currency }}</span>
                        <a-input-number
                          v-model="form.shippingCost"
                          :disabled="!form.withCourier || form.isIntegratedCourier"
                          class="nostep flex-grow-1"
                          size="default"
                          autocomplete="off"
                          :precision="precision"
                          :formatter="value => inputFormatter(value)"
                          :parser="value => inputParser(value)"
                          @keypress="numberOnly"
                          @change="form.shippingCost = Math.abs($event)"
                        />
                      </a-input-group>
                    </a-form-model-item>
                  </div>
                </div>

                <div class="summary-row mb-2">
                  <span class="summary-label text-muted">{{ $t('manualOrder.insurance_cost') }}</span>
                  <div class="summary-input-wrap">
                    <a-form-model-item class="m-0" prop="insuranceCost">
                      <a-input-group compact class="summary-input-group">
                        <span class="input-addon-currency">{{ currency }}</span>
                        <a-input-number
                          v-model="form.insuranceCost"
                          :disabled="!form.withCourier || form.isIntegratedCourier"
                          class="nostep flex-grow-1"
                          size="default"
                          autocomplete="off"
                          :precision="precision"
                          :formatter="value => inputFormatter(value)"
                          :parser="value => inputParser(value)"
                          @keypress="numberOnly"
                          @change="form.insuranceCost = Math.abs($event)"
                        />
                      </a-input-group>
                    </a-form-model-item>
                  </div>
                </div>

                <a-divider class="my-3" />

                <div class="p-3 bg-light rounded mb-4 border">
                  <div class="text-muted small text-uppercase font-weight-bold mb-1">
{{ $t('manualOrder.grand_total') }}
</div>
                  <div class="text-primary font-weight-bold tabular-nums" style="font-size: 22px;">
                    {{ `${currency} ${toCurrency(computedGrandTotal)}` }}
                  </div>
                </div>
</div>
            </a-card>
            </div>

            <div v-if="!isDetail" class="sidebar-sticky-actions px-3">
              <a-button
                html-type="submit"
                type="primary"
                size="large"
                block
                :loading="isLoading"
              >
                {{ $t('utils.save') }}
              </a-button>

              <a-button
                type="default"
                size="large"
                block
                class="mt-2"
                :disabled="isLoading"
                @click="handleCancelOrder"
              >
                {{ $t('utils.cancel') }}
              </a-button>
            </div>
          </div>
        </a-col>
      </a-row>

      <a-modal
        v-model="showModalCart"
        :title="$t('manualOrder.cart_title')"
        width="1100px"
        :footer="null"
        centered
        @cancel="handleFinishCartModal"
      >
        <a-spin size="large" :spinning="calculatingShippingCost" :indicator="customIndicatorCalculatingShippingCost">
          <a-spin :indicator="customIndicatorFormCart" :spinning="!form.warehouseId">
            <div>
              <div class="d-flex align-items-center justify-content-between mb-3">
                <div class="d-flex">
                  <a-button
                    type="primary"
                    @click="handleOpenCartAddItem"
                  >
                    {{ $t('manualOrder.add_item') }}
                  </a-button>
                  <a-button
                    class="ml-2"
                    type="primary"
                    @click="handleOpenBulkImportModal"
                  >
                    {{ $t('manualOrder.bulk_item_import') }}
                  </a-button>
                </div>
                <div>{{ $t('dashboardContent.total_sku') }}: <b>{{ computedValidSKU || 0 }}</b></div>
              </div>

              <a-alert
                v-if="form.warehouseId && !inventories.length && !search"
                class="mb-2"
                style="font-size: 13px"
                :message="$t('manualOrder.no_product_in_warehouse')"
                type="error"
                show-icon
              />

              <a-table
                :columns="columns"
                :row-key="(record, index) => index"
                :data-source="itemList"
                :loading="isLoading"
                :pagination="false"
                :scroll="{ y: 400, x: 1000 }"
              >
                <template slot="action1" slot-scope="text, record">
                  <div @click="onDeleteItem(record)">
                    <TrashIcon style="color: #E00000; cursor:pointer" />
                  </div>
                </template>
                <template slot="sku" slot-scope="text, record">
                  <a-select
                    v-model="record.catalog_id"
                    size="large"
                    class="select-antd-default"
                    style="width: 100%"
                    :placeholder="$t('manualOrder.product_placeholder')"
                    show-search
                    :label-in-value="false"
                    :not-found-content="fetching ? undefined : null"
                    :filter-option="false"
                    @change="getProductUOM($event, record)"
                    @blur="handleResetSearch"
                    @search="handleSearch"
                    @popupScroll="handleCatalogPopupScroll"
                  >
                    <a-spin v-if="fetching" slot="notFoundContent" size="small" />
                    <a-select-option
                      v-for="item in inventories"
                      :key="item.id"
                      :value="`${item.id}.${item.product_id}`"
                      :disabled="selectedSKU.includes(item.sku)"
                    >
                      <div class="d-flex flex-column product-select">
                        <div>{{ item?.title }}</div>
                        <div class="sku-desc">
                          {{ 'SKU : ' + ellipsisText(item?.sku) }}
                        </div>
                      </div>
                    </a-select-option>
                    <a-select-option v-if="catalogFetching" key="__loading__" disabled>
                      <div style="text-align: center; padding: 4px 0;">
                        <a-spin size="small" />
                      </div>
                    </a-select-option>
                    <a-select-option
                      v-else-if="inventories.length > 0 && inventories.length >= catalogTotalRow"
                      key="__all_loaded__"
                      disabled
                    >
                      <div style="text-align: center; padding: 4px 0; color: #aaa; font-size: 12px;">
                        {{ $t('manualOrder.all_products_loaded') }}
                      </div>
                    </a-select-option>
                  </a-select>
                  <template v-if="record?.availability < 1">
                    <a-alert
                      style="font-size: 13px"
                      :message="$t('manualOrder.stock_unavailable')"
                      type="error"
                      show-icon
                    />
                  </template>
                </template>
                <template slot="msrp" slot-scope="text">
                  <div>
                    {{ `${currency} ${toCurrency(text)}` }}
                  </div>
                </template>
                <template slot="selling_price" slot-scope="text, record, index">
                  <a-input-group compact>
                    <div class="addon-before">
                      {{ currency }}
                    </div>
                    <a-input-number
                      v-model="record.selling_price"
                      :disabled="!record.catalog_id"
                      size="large"
                      style="width: 65%"
                      autocomplete="off"
                      :precision="precision"
                      :formatter="value => inputFormatter(value)"
                      :parser="value => inputParser(value)"
                      placeholder="0"
                      @change="onChangeInputCart($event, index, 'selling_price', defaultMaxPrice)"
                    />
                  </a-input-group>
                </template>
                <template slot="product_discount" slot-scope="text, record, index">
                  <a-input-group compact>
                    <div class="addon-before">
                      {{ currency }}
                    </div>
                    <a-input-number
                      v-model="record.product_discount"
                      :disabled="!record.catalog_id"
                      size="large"
                      style="width: 65%"
                      autocomplete="off"
                      placeholder="0"
                      :precision="precision"
                      :formatter="value => inputFormatter(value)"
                      :parser="value => inputParser(value)"
                      @change="onChangeInputCart($event, index, 'product_discount', record.selling_price)"
                    />
                  </a-input-group>
                </template>
                <template slot="qty" slot-scope="text, record, index">
                  <div class="d-flex flex-column pt-1" style="gap: 4px;">
                    <div class="d-flex justify-items-center" style="gap: 10px;">
                      <a-tooltip trigger="click" :title="`Stok tersedia: ${record?.availability}`" color="red">
                        <a-input-number
                          v-model="record.quantity"
                          :disabled="!record.catalog_id"
                          size="large"
                          class="nostep w-100"
                          :class="{ 'border-danger': record.catalog_id && (!record.quantity || Number(record.quantity) < 1) }"
                          autocomplete="off"
                          placeholder="0"
                          :max="defaultMaxQuantity"
                          :precision="0"
                          :formatter="value => inputFormatter(value, 'quantity')"
                          :parser="value => inputParser(value, 'quantity')"
                          @change="onChangeInputCart($event, index, 'quantity', defaultMaxQuantity)"
                        />
                      </a-tooltip>
                      <a-select v-model="record.uom" class="w-100" size="large" :disabled="!record.catalog_id">
                        <a-select-option v-for="level in record.levels" :key="level">
                          {{ level }}
                        </a-select-option>
                      </a-select>
                    </div>
                    <div v-if="record.catalog_id && (!record.quantity || Number(record.quantity) < 1)" class="text-danger extra-small" style="font-size: 11px;">
                      Qty minimal 1
                    </div>
                  </div>
                </template>
                <template slot="total_price" slot-scope="text">
                  <div>
                    {{ `${currency} ${toCurrency(text)}` }}
                  </div>
                </template>
              </a-table>

              <div class="mt-3 d-flex align-items-center justify-content-between">
                <div>
                  {{ $t('manualOrder.sub_total') }}: <b>{{ `${currency} ${toCurrency(computedSubTotal)}` }}</b>
                </div>
                <a-button type="primary" size="large" @click="handleFinishCartModal">
                  Selesai
                </a-button>
              </div>
            </div>
          </a-spin>
        </a-spin>
      </a-modal>

      <div ref="targetRef" />
    </a-form-model>
    <ModalBulkItemImport
      :is-loading="isLoading"
      :visible="showModalBulkItemImport"
      :count-item-import="countItemImport"
      :count-success-import="countSuccessImport"
      :count-failed-import="countFailedImport"
      :on-close="() => showModalBulkItemImport = false"
      @handleSubmitModal="onNormalizeData"
    />
    <ModalFeedbackAfterImport
      :revised-data="listRevisedBulkItemImport"
      :is-loading="isLoading"
      :visible="showModalFeedbackAfterImport"
      :on-close="() => showModalFeedbackAfterImport = false"
    />
  </div>
</template>

<script>
import { createManualOrder, getStoreId, addManualOrderAttachment } from '@/api/sales'
import { getCatalogListGR, getUom } from '@/api/inventory'
import { getPriceRange } from '@/api/price'
import { getShippingCourier, getShippingService, getShippingDataLogapi, getShippingUnit } from '@/api/shipping'
import { getArea } from '@/api/data'

import { useInputNumber } from '@/composable/useInputNumber'
import { useDateTime } from '@/composable/useDateTime'
import TrashIcon from '@/components/Icons/TrashIcon.vue'
import debounce from 'lodash/debounce'
import { v4 as uuidv4 } from 'uuid';
import { useCurrency } from '@/composable/useCurrency'
import ModalBulkItemImport from '@/views/manual-order/ModalBulkItemImport.vue'
import ModalFeedbackAfterImport from '@/views/manual-order/ModalFeedbackAfterImport.vue'
import { getBusinessMetaData, getAreas } from '@/api/business'
import IndonesiaFlag from '@/components/Icons/IndonesiaFlag.vue'
import MalaysiaFlag from '@/components/Icons/MalaysiaFlag.vue'
import SingaporeFlag from '@/components/Icons/SingaporeFlag.vue'
import SouthAfricaFlag from '@/components/Icons/SouthAfricaFlag.vue'

const PAYMENT_TYPE = [
  {
    method: 'mandiri_va”',
    payment_name: 'VA Bank Mandiri',
    payment_type: 'VA',
  },
  {
    method: 'bca_va',
    payment_name: 'VA Bank Central Asia',
    payment_type: 'VA',
  },
  {
    method: 'bri_va',
    payment_name: 'VA Bank BRI',
    payment_type: 'VA',
  },
  {
    method: 'permata_va',
    payment_name: 'VA Bank Permata',
    payment_type: 'VA',
  },
  {
    method: 'other_va',
    payment_name: 'VA Bank Lainnya',
    payment_type: 'VA',
  },
  {
    method: 'mandiri_transfer”',
    payment_name: 'Bank Transfer Mandiri',
    payment_type: 'BANK_TRANSFER',
  },
  {
    method: 'bca_transfer',
    payment_name: 'Bank Transfer Central Asia',
    payment_type: 'BANK_TRANSFER',
  },
  {
    method: 'bri_transfer',
    payment_name: 'Bank Transfer BRI',
    payment_type: 'BANK_TRANSFER',
  },
  {
    method: 'permata_transfer',
    payment_name: 'Bank Transfer Permata',
    payment_type: 'BANK_TRANSFER',
  },
  {
    method: 'other_transfer',
    payment_name: 'Bank Transfer Lainnya',
    payment_type: 'BANK_TRANSFER',
  },
  {
    method: 'others',
    payment_name: 'Others',
    payment_type: 'OTHERS',
  },
]

export default {
  name: 'CreateDiscount',
  components: {
    TrashIcon,
    ModalBulkItemImport,
    ModalFeedbackAfterImport,
    IndonesiaFlag,
    MalaysiaFlag,
    SingaporeFlag,
    SouthAfricaFlag,
  },
  beforeRouteLeave(to, from, next) {
    
    
    
    next()
  },
  setup() {
    const { format, formatAbs, parseNumberOnly } = useCurrency()
    const { numberOnly, toNumber } = useInputNumber()
    const { parseISODate } = useDateTime()
    return {
      numberOnly,
      toNumber,
      parseISODate,
      format,
      formatAbs,
      parseNumberOnly,
    }
  },
  data() {
    return {
      defaultTier: 1,
      readyTier: 1,
      currTier: 1,
      currQuota: 0,
      sellerId: [],
      idPromo: -1,
      dateFormat: 'DD MMM YYYY HH:mm',
      isLoading: false,
      isDetail: false,
      fetching: false,
      endOpen: false,
      visible: false,
      areaAll: false,
      distributorAll: false,
      categoryAll: false,
      customerAll: false,
      itemList: [],
      warehouseList: [],
      warehouseSearching: false,
      inventories: [],
      catalogPage: 1,
      catalogTotalRow: 0,
      catalogFetching: false,
      fileList: [],
      paymentList: PAYMENT_TYPE,
      totalProduct: null,
      rules: {
        orderNumber: [{ required: false, trigger: 'change' }],
        paymentType: [{ required: true, trigger: 'change', message: this.$t('manualOrder.select_payment_method') }],
        orderDate: [{ required: true, trigger: 'change', message: this.$t('manualOrder.select_order_date') }],
        firstName: [{ required: true, trigger: 'change', message: this.$t('manualOrder.input_first_name') }],
        lastName: [{ required: true, trigger: 'change', message: this.$t('manualOrder.input_last_name') }],
        email: [
          {
            required: false,
            trigger: 'change',
          },
          {
            trigger: 'change',
            validator: (rule, value, callback) => {
              if (!value) {
                callback()
              } else if (value.length > 255) {
                callback(new Error(this.$t('manualOrder.max_char_warning', { max: 255 })))
              } else {
                callback()
              }
            },
          },
        ],
        phoneNumber: [
          {
            required: false,
            trigger: 'change',
          },
          {
            trigger: 'change',
            validator: (rule, value, callback) => {
              if (!value) {
                callback()
              } else if (value.length > 255) {
                callback(new Error(this.$t('manualOrder.max_char_warning', { max: 255 })))
              } else {
                callback()
              }
            },
          },
        ],
        country: [{ required: false, trigger: 'change', message: this.$t('manualOrder.select_country') }],
        city: [{ required: true, trigger: 'change', message: this.$t('manualOrder.select_city') }],
        subdistrict: [{ required: true, trigger: 'change', message: this.$t('manualOrder.select_district') }],
        postcode: [{ required: true, trigger: 'change', message: this.$t('manualOrder.input_postcode') }],
        addressLine1: [{ required: true, trigger: 'change', message: this.$t('manualOrder.input_address') }],
        warehouseId: [{ required: true, trigger: 'change', message: this.$t('manualOrder.select_warehouse') }],
        courierName: [{ required: true, trigger: 'change', message: this.$t('manualOrder.select_courier') }],
        courierService: [{ required: true, trigger: 'change', message: this.$t('manualOrder.select_courier_service') }],
        awbNumber: [{ required: false, trigger: 'change' }],
      },
      initVal: {},
      search: '',
      overlayStyle: {
        width: '100px',
      },
      params: {
        page: 1,
        size: 1000,
      },
      codeCountry: [],
      countries: [],
      cities: [],
      subdistricts: [],
      billingCountries: [],
      billingCities: [],
      billingSubdistricts: [],
      couriers: [],
      couriersRAW: [],
      displayedCouriers: [],
      pageSizeCouriers: 10,
      pageCouriers: 1,
      loadingScrollCouriers: false,
      courierServices: [],
      listCourierIntegrated: [],
      fetchingCourierIntegrated: false,
      loadingCountries: false,
      loadingCities: false,
      loadingSubdistrict: false,
      form: {
        orderNumber: '',
        paymentType: '',
        orderDate: '',
        warehouseId: '',
        totalDiscount: 0,
        totalVoucher: 0,
        shippingCost: 0,
        insuranceCost: 0,
        firstName: '',
        lastName: '',
        email: '',
        extPhone: '',
        phoneNumber: '',
        attachmentUrl: '',
        country: undefined,
        city: undefined,
        subdistrict: undefined,
        postcode: '',
        addressLine1: '',
        withCourier: true,
        isIntegratedCourier: false,
        courierName: undefined,
        courierService: undefined,
        courierIntegrated: undefined,
        awbNumber: '',
        withLabelUrl: 'url',
        shippingUrlLabel: '',
        shippingOwnLabel: '',
        shippingFileLabel: '',
        sameWithShippingAddress: false,
        billingCountry: '',
        billingCity: '',
        billingSubdistrict: '',
        billingPostcode: '',
        billingAddressLine1: '',
      },
      subTotal: 0,
      grandTotal: 0,
      isCartCollapsed: false,
      showModalCart: false,
      showModalBulkItemImport: false,
      listRevisedBulkItemImport: [],
      showModalFeedbackAfterImport: false,
      countItemImport: 0,
      countSuccessImport: 0,
      countFailedImport: 0,
      isValidShippingUrlLabel: true,
      storeId: '',
      resMetaBusiness: [],
      totalWeight: 0,
      calculatingShippingCost: false,
      defaultMaxQuantity: 999999999,
      defaultMaxPrice: 999999999999,
      defaultMaxItem: 150,
      customIndicatorFormCourier: (
        <div class="w-100" style="left: 0; margin: 0;">
          <a-alert v-if="!form.subdistrict || !form.warehouseId" message={this.$t('manualOrder.shipping_warehouse_warning')} type="warning" show-icon />
        </div>
      ),
      customIndicatorFormCart: (
        <div class="w-100" style="left: 0; margin: 0;">
          <a-alert class="w-50" style="margin-inline: auto;" v-if="!form.warehouseId" message={this.$t('manualOrder.warehouse_product_warning')} type="warning" show-icon />
        </div>
      ),
      customIndicatorCalculatingShippingCost: (
        <div class="w-100" style="left: 0; margin: 0;">
          <a-alert class="w-50 mt-2" style="margin-inline: auto;" message={this.$t('manualOrder.weight_change_calculating')} type="error" />
        </div>
      ),
    }
  },
  computed: {
    columns() {
      return [
        {
          title: '',
          dataIndex: 'action1',
          key: 'action1',
          width: 50,
          fixed: 'left',
          scopedSlots: {
            customRender: 'action1',
          },
        },
        {
          title: this.$t('common.sku'),
          dataIndex: 'catalog_id',
          key: 'catalog_id',
          fixed: 'left',
          width: 250,
          scopedSlots: {
            customRender: 'sku',
          },
        },
        {
          title: this.$t('manualOrder.msrp'),
          dataIndex: 'msrp',
          key: 'msrp',
          width: 130,
          scopedSlots: {
            customRender: 'msrp',
          },
        },
        {
          title: this.$t('manualOrder.selling_price'),
          dataIndex: 'selling_price',
          key: 'selling_price',
          width: 250,
          scopedSlots: {
            customRender: 'selling_price',
          },
        },
        {
          title: this.$t('manualOrder.product_discount'),
          dataIndex: 'product_discount',
          key: 'product_discount',
          width: 250,
          scopedSlots: {
            customRender: 'product_discount',
          },
        },
        {
          title: this.$t('manualOrder.qty'),
          dataIndex: 'qty',
          key: 'qty',
          width: 300,
          scopedSlots: {
            customRender: 'qty',
          },
        },
        {
          title: this.$t('manualOrder.total'),
          dataIndex: 'total_price',
          key: 'total_price',
          width: 250,
          scopedSlots: {
            customRender: 'total_price',
          },
        },
      ]
    },
    businessId() {
      return this.$store.state.user.restriction_base === 'Warehouse'
        ? Object.keys(this.$store.getters['user/businessListObjectByKey'])[0]
        : this.$route.query.business_id
    },
    business() {
      return Object.keys(this.$store.getters['user/businessListObjectByKey'])[0]
    },
    countryId() {
      return this.$store.getters['user/businessListObjectByKey'][this.businessId]?.area_id || 228
    },
    currency() {
      return this.$store.getters['user/businessListObjectByKey'][this.businessId]?.currency?.currency_code || 'IDR'
    },
    isPrinciple() {
      return ['business owner', 'supervisor', 'store operator', 'principle'].includes(this.$store.state.user.role_name?.toLowerCase())
    },
    selectedSKU() {
      return this.itemList.filter((obj2) => obj2.catalog_id).map((obj2) => obj2.sku)
    },
    computedSubTotal() {
      const validItems = this.itemList.filter((i) => i.catalog_id)
      if (validItems.length) {
        const sumTotalPrice = validItems.reduce((sum, { total_price }) => { return sum + (total_price || 0) }, 0)
        return sumTotalPrice
      }
      return 0
    },
    computedGrandTotal() {
      const validItems = this.itemList.filter((i) => i.catalog_id)
      if (validItems.length) {
        const sumTotalPrice = validItems.reduce((sum, { total_price }) => { return sum + (total_price || 0) }, 0)
        return sumTotalPrice - ((this.form?.totalDiscount || 0) + (this.form?.totalVoucher || 0)) + (this.form?.shippingCost || 0) + (this.form?.insuranceCost || 0)
      }
      return 0
    },
    computedValidSKU() {
      return this.itemList.filter((obj) => obj.catalog_id)?.length
    },
    computedCourierType() {
      return [this.form.withCourier, this.form.isIntegratedCourier]
    },
    precision() {
      return this.currency === 'IDR' ? 0 : 2
    },
  },
  watch: {
    businessId() {
      this.init()
      this.getWarehouseList()
    },
    'form.courierIntegrated': {
      deep: true,
      immediate: false,
      handler: function (value) {
        if (value >= 0) {
          const { price: shippingPrice, insurance_price: insurancePrice } = this.listCourierIntegrated[value]
          this.form.shippingCost = shippingPrice || 0
          this.form.insuranceCost = insurancePrice || 0
        }
      },
    },
    itemList: {
      deep: true,
      immediate: false,
      handler: debounce(function (value) {
        if (value?.length > 0) {
          let tempTotalWeight = 0
          value.forEach((item) => {
            if (!item?.product_units?.length > 0) {
              return
            }
            const findWeightByUOM = item.product_units.find((unit) => unit.unit?.toUpperCase() === item.uom?.toUpperCase())
            if (findWeightByUOM) {
              tempTotalWeight = tempTotalWeight + (findWeightByUOM.weight * item.quantity)
            }
          })
          if (tempTotalWeight !== this.totalWeight) {
            this.totalWeight = tempTotalWeight
            if (this.form.isIntegratedCourier && this.form.courierIntegrated) {
              this.calculatingShippingCost = true
              this.fetchShippingDataLogapi()
            }
          }
        }
      }, 500),
    },
    search: {
      deep: true,
      immediate: true,
      handler: function () {
        
        this.fetchInventory()
        
      },
    },
    'form.shippingUrlLabel': {
      deep: true,
      immediate: true,
      handler: debounce(function (value) {
        if (value) {
          this.isValidShippingUrlLabel = this.validateShippingLabelUrl(value)
        } else {
          this.isValidShippingUrlLabel = true
        }
      }, 500),
    },
    'computedCourierType': {
      deep: true,
      immediate: true,
      handler: function ([ returnWithCourier, returnIsIntegratedCourier ]) {
        if (returnWithCourier) {
          this.rules = {
            ...this.rules,
            country: [{ required: true, trigger: 'change' }],
            city: [{ required: true, trigger: 'change' }],
            subdistrict: [{ required: true, trigger: 'change' }],
            postcode: [{ required: true, trigger: 'change' }],
            addressLine1: [{ required: true, trigger: 'change' }],
            courierName: [{ required: true, trigger: 'change' }],
            courierService: [{ required: true, trigger: 'change' }],
            awbNumber: [{ required: false, trigger: 'change' }],
            courierIntegrated: [{ required: false, trigger: 'change' }],
          }
        } else if (returnIsIntegratedCourier) {
          this.rules = {
            ...this.rules,
            country: [{ required: true, trigger: 'change' }],
            city: [{ required: true, trigger: 'change' }],
            subdistrict: [{ required: true, trigger: 'change' }],
            postcode: [{ required: true, trigger: 'change' }],
            addressLine1: [{ required: true, trigger: 'change' }],
            courierName: [{ required: false, trigger: 'change' }],
            courierService: [{ required: false, trigger: 'change' }],
            awbNumber: [{ required: false, trigger: 'change' }],
            courierIntegrated: [{ required: true, trigger: 'change' }],
          }
        } else {
          this.form.sameWithShippingAddress = false
          this.form.shippingCost = 0
          this.form.insuranceCost = 0
          this.rules = {
            ...this.rules,
            country: [{ required: false, trigger: 'change' }],
            city: [{ required: false, trigger: 'change' }],
            subdistrict: [{ required: false, trigger: 'change' }],
            postcode: [{ required: false, trigger: 'change' }],
            addressLine1: [{ required: false, trigger: 'change' }],
            courierName: [{ required: false, trigger: 'change' }],
            awbNumber: [{ required: false, trigger: 'change' }],
            courierService: [{ required: false, trigger: 'change' }],
            billingCountry: [{ required: true, trigger: 'change' }],
            billingCity: [{ required: true, trigger: 'change' }],
            billingSubdistrict: [{ required: true, trigger: 'change' }],
            billingPostcode: [{ required: true, trigger: 'change' }],
            billingAddressLine1: [{ required: true, trigger: 'change' }],
          }
        }
      },
    },
  },
  created() {
    this.init()
    this.getWarehouseList()
    this.getAreaList()
  },
  methods: {
    inputFormatter(value, type = '') {
      if (type === 'quantity' || this.currency === 'IDR') return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    inputParser(value, type = '') {
      if (type === 'quantity' || this.currency === 'IDR') return value.replace(/,/g, '')
      return value.replace(/\$\s?|(,*)/g, '')
    },
    async fetchBusinessMeta() {
      const { data: resFetchBusinessMeta } = await getBusinessMetaData({
        business_id: this.businessId,
      })
      if (resFetchBusinessMeta?.length > 0) {
        this.resMetaBusiness = resFetchBusinessMeta
      } else {
        this.resMetaBusiness = []
      }
    },
    findMetaName(name) {
      if (this.resMetaBusiness.length === 0) {
        return ''
      }
      const foundMetaValue = this.resMetaBusiness.find((obj) => obj.meta_display === name || obj.meta_name === name)?.meta_value
      if (foundMetaValue){
        return JSON.parse(foundMetaValue)
      }
      return ''
    },
    init: debounce(function() {
      this.fetchBusinessMeta()
      this.fetchInventory()
      this.fetchShippingCourier()
    }, 500),
    handleOpenCartModal() {
      if (!this.form.warehouseId) {
        this.$refs.ruleForm.validateField('warehouseId')
        this.$message.warning(this.$t('manualOrder.warehouse_product_warning') || 'Silakan pilih Gudang terlebih dahulu')
        return
      }
      this.showModalCart = true
    },
    handleFinishCartModal() {
      this.itemList = this.itemList.filter((item) => !!item.catalog_id)

      const invalidQtyItem = this.itemList.find((item) => !item.quantity || Number(item.quantity) < 1)

      if (invalidQtyItem) {
        this.$notification.error({
          message: this.$t('manualOrder.fail_create_order') || 'Gagal menyimpan keranjang',
          description: this.$t('manualOrder.check_qty_warning') || 'Jumlah (Qty) produk harus minimal 1',
        })
        this.showModalCart = true
        return
      }

      this.showModalCart = false
    },
    handleOpenBulkImportModal() {
      if (!this.form.warehouseId) {
        this.$refs.ruleForm.validateField('warehouseId')
        this.$message.warning(this.$t('manualOrder.warehouse_product_warning') || 'Silakan pilih Gudang terlebih dahulu')
        return
      }
      this.showModalBulkItemImport = true
    },
    handleOpenCartAddItem() {
      if (!this.form.warehouseId) {
        this.$refs.ruleForm.validateField('warehouseId')
        this.$message.warning(this.$t('manualOrder.warehouse_product_warning') || 'Silakan pilih Gudang terlebih dahulu')
        return
      }
      if (!this.inventories.length) {
        this.$message.warning(this.$t('manualOrder.no_product_in_warehouse') || 'Tidak ada stok produk di gudang ini')
        return
      }
      this.addItem()
    },
    onChangeWarehouse() {
      this.itemList = []
      this.fetchInventory()
    },
    validateShippingLabelUrl(url) {
      const urlRegex = /^(https?:\/\/)?(www\.)?(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(\/[a-z0-9\-._~%!$&'()*+,;=:@]*)*(\?[a-z0-9\-._~%!$&'()*+,;=:@/?]*)?(#[a-z0-9\-._~%!$&'()*+,;=:@/?]*)?$/i;
      return urlRegex.test(url);
    },
    onNormalizeData(importedData) {
      this.countItemImport = 0
      this.countSuccessImport = 0
      this.countFailedImport = 0
      this.isLoading = true
      this.listRevisedBulkItemImport = []
      const normalizeData = []
      importedData.forEach((obj) => {
        const status = []
        const newObj = Object.fromEntries(
          Object.entries(obj).map(([key, val]) => [key.replaceAll(' ', '_').toLowerCase(), val]),
        );
        const isDuplicatedSKU = normalizeData.find((obj2) => obj2.sku === newObj.sku)
        if (isDuplicatedSKU) {
          return
        }
        if (!/^\d+$/.test(newObj.quantity) || !newObj?.sku || !newObj?.quantity) {
          this.countFailedImport = this.countFailedImport + 1
          status.push('data_not_valid')
        }
        if (status.length) {
          this.listRevisedBulkItemImport.push({
            ...newObj,
            status: status,
          })
          this.countItemImport = this.countItemImport + 1
          return
        }
        this.countItemImport = this.countItemImport + 1
        normalizeData.push(newObj)
      })
      this.onSubmitBulkItemImport(normalizeData)
    },
    onSubmitBulkItemImport(importedData) {
      const promises = []
      const tempItemList = this.itemList
      const { warehouseId } = this.form
      if (!warehouseId) {
        this.isLoading = false
        return
      }
      importedData.map((obj) => {
        const isDuplicatedSKU = tempItemList.find((obj2) => obj2.sku === obj.sku)
        if (isDuplicatedSKU) {
          return
        }
        const bodyValue = {
          business_id: this.businessId,
          warehouse_id: warehouseId,
          search: obj.sku,
          page: 1,
          limit: 20,
        }
        promises.push(
          getCatalogListGR(bodyValue),
        )
      })

      Promise.all(promises)
        .then(async (res) => {
          await Promise.all(res.map(async (obj, index) => {
            const { data, total_row } = obj.data
            if (total_row > 0) {
              const findSpecificCatalog = data.find((item) => String(item.sku) === String(importedData[index].sku))
              if (findSpecificCatalog) {
                const selecetedCatalog = findSpecificCatalog
                const selectedImagePath = JSON.parse(selecetedCatalog.images).at(0)
                const newRecord = {
                  item_reference_id: tempItemList.length + index,
                  catalog_id: `${selecetedCatalog.id}.${selecetedCatalog.product_id}`,
                  catalog_title: selecetedCatalog.title,
                  quantity: importedData[index].quantity,
                  uom: importedData[index].uom,
                  sku: importedData[index].sku,
                  batch_number: '',
                  expired_date: null,
                  stock_type: 'AV',
                  base_quantity: '',
                  base_uom: '',
                  serial_number: '',
                  levels: [],
                  units: {},
                  availability: selecetedCatalog.availability,
                  showimg_url: selectedImagePath?.imagePath,
                  currency: this.currency,
                }
                const { data: { data: uomLevels } } = await getUom({
                  id: selecetedCatalog.product_id,
                  params: {
                    business_id: this.businessId,
                  },
                })
                if (uomLevels) {
                  let levels = []
                  uomLevels.units.forEach((item, index) => {
                    levels.push(item.unit)
                    newRecord.units[item.unit] = {
                      numerator: item.numerator,
                      denumerator: item.denumerator,
                    }
                    if(item.numerator == 1 && item.denumerator == 1) newRecord.base_uom = item.unit
                  })
                  newRecord.levels = levels
                } else {
                  newRecord.uom = ''
                  newRecord.base_uom = ''
                  newRecord.levels = []
                  newRecord.units = {}
                }
                const isValidUOM = newRecord.levels.find((obj) => obj === newRecord.uom)
                if (isValidUOM) {
                  const { data: { data: resPriceRange } } = await getPriceRange({
                    product_id: selecetedCatalog.product_id,
                    business_id: this.businessId,
                  })
                  if (resPriceRange.length > 0) {
                    const tempPrice = resPriceRange.find((obj) => obj.catalog_id === selecetedCatalog.id)?.price
                    const msrp = tempPrice.find((obj) => obj.uom === newRecord.uom)?.value
                    newRecord.price = tempPrice
                    newRecord.msrp = msrp
                    newRecord.selling_price = importedData[index].selling_price
                    newRecord.product_discount = importedData[index].product_discount
                    newRecord.total_price = (newRecord.selling_price - (newRecord.product_discount || 0)) * newRecord.quantity
                  }
                  const isExistOnInventories = this.inventories.find((obj) => obj.id === selecetedCatalog.id)
                  if (!isExistOnInventories) {
                    this.inventories.push(selecetedCatalog)
                  }
                  this.itemList.push(newRecord)
                  this.countSuccessImport = this.countSuccessImport + 1
                  this.onChangeQuantity(`${newRecord.quantity}`, newRecord)
                } else {
                  this.countFailedImport = this.countFailedImport + 1
                  this.listRevisedBulkItemImport.push({
                    ...importedData[index],
                    status: ['uom_not_valid'],
                  })
                }
              } else {
                this.countFailedImport = this.countFailedImport + 1
                this.listRevisedBulkItemImport.push({
                  ...importedData[index],
                  status: ['sku_not_found'],
                })
              }
            } else {
              this.countFailedImport = this.countFailedImport + 1
              this.listRevisedBulkItemImport.push({
                ...importedData[index],
                status: ['sku_not_found'],
              })
            }
          }))
        })
        .catch(err => {
          console.error(err)
        })
        .finally(() => {
          this.showModalBulkItemImport = false
          this.isLoading = false
          if (this.listRevisedBulkItemImport.length) {
            this.showModalFeedbackAfterImport = true
            this.$notification.warning({
              message: this.$t('manualOrder.some_data_invalid'),
            })
          } else {
            this.$notification.success({
              message: this.$t('manualOrder.all_data_imported'),
            })
          }
        })
    },
    scrollToBottom() {
      if (!this.form.sameWithShippingAddress) {
        setTimeout(() => {
          this.$refs.targetRef.scrollIntoView({
            block: 'start',
            behavior: 'smooth',
          });
        }, 50)
        this.rules = {
          ...this.rules,
          billingCountry: [{ required: true, trigger: 'change' }],
          billingCity: [{ required: true, trigger: 'change' }],
          billingSubdistrict: [{ required: true, trigger: 'change' }],
          billingPostcode: [{ required: true, trigger: 'change' }],
          billingAddressLine1: [{ required: true, trigger: 'change' }],
        }
      } else {
        this.rules = {
          ...this.rules,
          billingCountry: [{ required: false, trigger: 'change' }],
          billingCity: [{ required: false, trigger: 'change' }],
          billingSubdistrict: [{ required: false, trigger: 'change' }],
          billingPostcode: [{ required: false, trigger: 'change' }],
          billingAddressLine1: [{ required: false, trigger: 'change' }],
        }
      }
    },
    toCurrency(value) {
      return `${Intl.NumberFormat('en-US').format(value || 0)}`
    },
    async fetchShippingCourier() {
      const { data } = await getShippingCourier()
      this.couriersRAW = data?.data || []
      this.couriers = data?.data || []
      this.displayedCouriers = this.couriers?.slice(0, this.pageSizeCouriers)
    },
    handleScrollCouriers(e) {
      const bottom = e.target.scrollHeight <= e.target.scrollTop + e.target.clientHeight + 10;
      if (bottom && !this.loadingScrollCouriers) {
        this.loadingScrollCouriers = true;
        setTimeout(() => {
          this.pageCouriers += 1;
          const start = this.pageSizeCouriers * (this.pageCouriers - 1);
          const end = start + this.pageSizeCouriers;
          this.displayedCouriers = this.couriers.slice(0, end);
          this.loadingScrollCouriers = false;
        }, 500);
      }
    },
    async fetchShippingService() {
      const { data } = await getShippingService({
        courier_id: this.form.courierName,
      })
      this.courierServices = data?.data || []
    },
    async fetchShippingDataLogapi() {
      this.fetchingCourierIntegrated = true
      const { data: resStoreId } = await getStoreId({
        business_id: this.businessId,
      })
      if (!resStoreId.hash_id) {
        this.fetchingCourierIntegrated = false
        this.storeId = ''
        this.$notification.error({
          description: this.$t('manualOrder.error_get_store_id'),
          message: this.$t('manualOrder.error_occurred'),
        })
        return
      }
      const payload = {
        dest_id: this.form.subdistrict,
        weight: this.totalWeight,
        seller_id: resStoreId.hash_id,
        total: 1,
        q_warehouse_id: this.form.warehouseId,
        q_channel_id: this.$route.params.id,
      }
      this.storeId = resStoreId.hash_id
      getShippingDataLogapi(payload)
        .then(({ data: { data: response }, ...rest }) => {
          if (response.courier?.length > 0) {
            this.listCourierIntegrated = response.courier
            if (this.calculatingShippingCost) {
              this.calculatingShippingCost = false
              this.$notification.success({
                message: this.$t('manualOrder.success_update_shipping_cost'),
              })
            }
          } else {
            this.listCourierIntegrated = []
            if (this.calculatingShippingCost) {
              this.calculatingShippingCost = false
              this.$notification.error({
                message: this.$t('manualOrder.error_calculating_shipping_cost'),
              })
            }
          }
        })
        .catch(() => {
          this.listCourierIntegrated = []
          if (this.calculatingShippingCost) {
            this.calculatingShippingCost = false
            this.$notification.error({
              message: this.$t('manualOrder.error_calculating_shipping_cost'),
            })
          }
        });
      this.fetchingCourierIntegrated = false
    },
    filterOptionCity(input, option) {
      return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
    },
    searchOptionCourier: debounce(function(input) {
      if (!input) {
        this.couriers = this.couriersRAW
        this.displayedCouriers = this.couriers?.slice(0, this.pageSizeCouriers)
      } else {
        const filteredOptionCourier = this.couriersRAW.filter(courier => 
          courier?.courier_name?.toLowerCase().includes(input.toLowerCase()),
        );
        this.couriers = filteredOptionCourier
        this.displayedCouriers = filteredOptionCourier?.slice(0, this.pageSizeCouriers)
      }
    }, 500),
    disabledDate(current) {
      return (current && current > this.$moment().endOf('day')) || (current && current.valueOf() < this.$moment().subtract(3, 'day'))
    },
    getWarehouseList() {
      this.$store
        .dispatch('warehouse/GETWAREHOUSELIST', {
          business_id: this.businessId,
          warehouse_id: this.$store.state.user.restriction_base === 'Warehouse'
            ? this.$route.query.warehouse_id
            : undefined,
          page: 1,
          size: 150,
        })
        .then(res => {
          this.warehouseList = res
          if(res?.length == 1) this.form.warehouseId = res[0].id
        })
        .catch(err => {
          console.error(err)
          this.warehouseList = []
        })
    },
    handleWarehouseSearch: debounce(function(query) {
      this.warehouseSearching = true
      this.$store
        .dispatch('warehouse/GETWAREHOUSELIST', {
          business_id: this.businessId,
          warehouse_id: this.$store.state.user.restriction_base === 'Warehouse'
            ? this.$route.query.warehouse_id
            : undefined,
          query: query || undefined,
          page: 1,
          size: 150,
        })
        .then(res => {
          this.warehouseList = res
        })
        .catch(err => {
          console.error(err)
        })
        .finally(() => {
          this.warehouseSearching = false
        })
    }, 400),
    async getAreaList() {
      this.loadingCountries = true
      await getAreas({
        business_id: this.businessId,
        params: {
          parent_id: 0,
          is_active: true,
        },
      })
      .then(({ data }) => {
        let areaList = []
        let phoneList = []
        data?.forEach(item => {
          areaList.push({
            ...item,
            value: item.id,
            label: item.area_name,
          })
          phoneList.push({
            value: item.area_phone,
            label: `+${item.area_phone}`,
          })
        })
        this.countries = areaList
        this.billingCountries = areaList
        this.codeCountry = phoneList
      })
      .catch(() => {
        this.countries = []
        this.billingCountries = []
        this.codeCountry = []
      })
      .finally(() => {
        this.form.country = this.countryId
        this.form.billingCountry = this.countryId
        this.form.extPhone = this.countries?.find(country => country.id == this.countryId)?.area_phone || '62'
        this.loadingCountries = false
      })
    },
    async getCities(isBilling) {
      this.loadingCities = true
      await getArea({
        businessId: this.$route.query.business_id,
        params: {
          parentId: isBilling
            ? this.form.billingCountry
            : this.form.country,
        },
      })
      .then(res => {
        const list = res?.data?.list
        if (list) {
          if (isBilling) {
            this.billingCities = list
          } else {
            this.cities = list
          }
        }
      })
      .catch(() => {})
      .finally(() => this.loadingCities = false)
    },
    onChangeCountry(isBilling) {
      if (isBilling) {
        this.billingCities = []
        this.form.billingCity = undefined
      } else {
        this.cities = []
        this.form.city = undefined
      }
      this.onChangeCity(isBilling)
    },
    onChangeCity(isBilling) {
      this.loadingSubdistrict = true
      if (isBilling) {
        this.billingSubdistricts = []
        this.form.billingSubdistrict = undefined
      } else {
        this.subdistricts = []
        this.form.subdistrict = undefined
      }
      this.onChangeDistrict(isBilling)
    },
    onChangeDistrict(isBilling) {
      if (isBilling) {
        const findSelectedSubdistrict = this.billingSubdistricts?.find((obj) => obj.value === this.form.billingSubdistrict)
        this.form.billingPostcode = findSelectedSubdistrict?.postcode
      } else {
        const findSelectedSubdistrict = this.subdistricts?.find((obj) => obj.value === this.form.subdistrict)
        this.form.postcode = findSelectedSubdistrict?.postcode
      }
    },
    async getSubdistrict(isBilling) {
      this.loadingSubdistrict = true
      await getArea({
        businessId: this.$route.query.business_id,
        params: {
          parentId: isBilling
            ? this.form.billingCity
            : this.form.city,
        },
      })
      .then(res => {
        const list = res?.data?.list
        if (list) {
          if (isBilling) {
            this.billingSubdistricts = list
          } else {
            this.subdistricts = list
          }
        }
      })
      .catch(() => {})
      .finally(() => this.loadingSubdistrict = false)
    },
    formatterValidateMaxValue(value, max) {
      if (value === '-') {
        return ''
      }
      if (value && value < 0) {
        value = Math.abs(value)
      }
      if (value > max) {
        value = max
      }
      return this.format(value)
    },
    onChangeDiscAndVoucherMaxValue(value, max, field) {
      value = !Number(value)
        ? 0
        : value
      if (value && value < 0) {
        value = Math.abs(value)
      }
      if (value > max) {
        value = max
      }
      this.form[field] = value
    },
    onChangeInputCart(value, index, field, max) {
      const currentItemList = this.itemList[index]
      if (!currentItemList) return

      value = !Number(value)
        ? 0
        : Number(value) && field === 'quantity'
        ? parseInt(value)
        : value

      if (value < 0) {
        value = Math.abs(value)
      } else if (value > max) {
        value = max
      }

      if (field === 'product_discount' && value > currentItemList.selling_price) {
        value = currentItemList.selling_price
      } else if (field === 'quantity' && value > this.defaultMaxQuantity) {
        value = this.defaultMaxQuantity
      } else if (field === 'selling_price' && value > this.defaultMaxPrice) {
        value = this.defaultMaxPrice
      }

      currentItemList[field] = value
      currentItemList.total_price = ((currentItemList.selling_price || 0) - (currentItemList.product_discount || 0)) * (currentItemList.quantity || 0)
    },
    ellipsisText(text) {
      if(!text) return '-'
      return text.length > 29 ? `${text.substring(0, 26)}...` : text
    },
    onSubmit() {
      this.$refs.ruleForm.validate(async (valid) => {
        if (valid) {
          if (!this.isValidShippingUrlLabel) {
            this.$notification.error({
              message: this.$t('manualOrder.fail_create_order'),
              description: this.$t('manualOrder.input_valid_url'),
            })
            this.isLoading = false
            return
          }
          let checkItemIsEmpty = false
          this.itemList?.forEach(item => {
            if (!item.sku) {
              checkItemIsEmpty = true
            }
          })
          if (this.itemList.length === 0) {
            this.$notification.error({
              message: this.$t('manualOrder.fail_create_order'),
              description: this.$t('manualOrder.add_item_min_1'),
            })
            this.isLoading = false
            return
          }
          if (checkItemIsEmpty) {
            this.$notification.error({
              message: this.$t('manualOrder.fail_create_order'),
              description: this.$t('manualOrder.item_not_filled'),
            })
            this.isLoading = false
            return
          }
          this.isLoading = true
          this.onSubmitSales()
        }
      })
    },
    onSubmitSales: debounce(async function() {
      const {
        firstName,
        lastName,
        email,
        extPhone,
        phoneNumber,
        attachmentUrl,
        addressLine1,
        postcode,
        country,
        city,
        subdistrict,
        shippingCost,
        insuranceCost,
        awbNumber,
        orderNumber,
        warehouseId,
        orderDate,
        paymentType,
        totalDiscount,
        totalVoucher,
        shippingUrlLabel,
        withLabelUrl,
        withCourier,
        courierService,
        sameWithShippingAddress,
        billingAddressLine1,
        billingPostcode,
        billingCountry,
        billingCity,
        billingSubdistrict,
        isIntegratedCourier,
        courierIntegrated,
      } = this.form

      const { params } = this.$route
      const selectedShippingCountry = this.countries.find((obj) => obj.value === country)
      const selectedShippingCity = country != '228'
        ? city
        : this.cities.find((obj) => obj.value === city)
      const selectedShippingSubdistrict = country != '228'
        ? subdistrict
        : this.subdistricts.find((obj) => obj.value === subdistrict)
      let payloadBillingAddress
      let payloadShippingAddress = {
        line1: addressLine1,
        line2: null,
        postal_code: postcode,
        country: selectedShippingCountry?.label,
        province: country != '228'
          ? selectedShippingSubdistrict
          : selectedShippingCity?.label.split(' - ')[0],
        city: country != '228'
          ? selectedShippingCity
          : selectedShippingCity?.label.split(' - ')[1],
        district: country != '228'
          ? null
          : selectedShippingSubdistrict?.label.split(' - ')[0],
        sub_district: country != '228'
          ? null
          : selectedShippingSubdistrict?.label.split(' - ')[1],
        country_id: country,
        city_id: country != '228'
          ? null
          : city,
        sub_district_id: country != '228'
          ? null
          : subdistrict,
      }
      if (sameWithShippingAddress) {
        payloadBillingAddress = payloadShippingAddress
      } else {
        const selectedBillingCountry = this.billingCountries.find((obj) => obj.value === billingCountry)
        const selectedBillingCity = billingCountry != '228'
          ? billingCity
          : this.billingCities.find((obj) => obj.value === billingCity)
        const selectedBillingSubdistrict = billingCountry != '228'
          ? billingSubdistrict
          : this.billingSubdistricts.find((obj) => obj.value === billingSubdistrict)
        payloadBillingAddress = {
          line1: billingAddressLine1,
          line2: null,
          postal_code: billingPostcode,
          country: selectedBillingCountry?.label,
          province: billingCountry != '228'
            ? selectedBillingSubdistrict
            : selectedBillingCity?.label.split(' - ')[0],
          city: billingCountry != '228'
            ? selectedBillingCity
            : selectedBillingCity?.label.split(' - ')[1],
          district: billingCountry != '228'
            ? null
            : selectedBillingSubdistrict?.label.split(' - ')[0],
          sub_district: billingCountry != '228'
            ? null
            : selectedBillingSubdistrict?.label.split(' - ')[1],
          country_id: billingCountry,
          city_id: billingCountry != '228'
            ? null
            : billingCity,
          sub_district_id: billingCountry != '228'
            ? null
            : billingSubdistrict,
        }
      }
      let selectedCourier
      if (withCourier) {
        selectedCourier = this.courierServices[courierService]
      }
      let finalLabelGenerated = null
      if (withLabelUrl === 'url' && shippingUrlLabel) {
        finalLabelGenerated = shippingUrlLabel
      }
      const SPU_SHIPPING_DETAIL = {
        id: uuidv4(),
        cost_type: null,
        courier: 'SPU',
        service_code: 'SPU',
        service_type: '',
        service_name: '',
        shipping_cost: {
          value: 0,
          currency: this.currency,
        },
        insurance_cost: {
          value: 0,
          currency: this.currency,
        },
        remarks: null,
        shipping_label_url: null,
        awb: "",
      }
      const selectedShippingDetail = {
        id: uuidv4(),
        cost_type: selectedCourier?.cost_type,
        courier: selectedCourier?.courier_name,
        service_code: selectedCourier?.service_code,
        service_type: selectedCourier?.service_type,
        service_name: selectedCourier?.service_name,
        shipping_cost: {
          value: shippingCost || 0,
          currency: this.currency,
        },
        insurance_cost: {
          value: insuranceCost || 0,
          currency: this.currency,
        },
        remarks: null,
        shipping_label_url: finalLabelGenerated,
        awb: awbNumber,
      }
      const finalShippingMethod = withCourier ? selectedShippingDetail : SPU_SHIPPING_DETAIL
      const selectedPayment = this.paymentList.find((obj) => obj.method === paymentType)
      let discountList = []
      let voucherList = []
      if (totalDiscount) {
        discountList.push({
          code: `test_discount_hardcode_${this.$moment().format('DDMMYYYY')}`,
          name: `TEST DISCOUNT HARDCODE ${this.$moment().format('DDMMYYYY')}`,
          type: 'transaction',
          value: totalDiscount || 0,
          source: 'seller',
        })
      }
      if (totalVoucher) {
        voucherList.push({
          code: `test_voucher_hardcode_${this.$moment().format('DDMMYYYY')}`,
          name: `TEST VOUCHER HARDCODE ${this.$moment().format('DDMMYYYY')}`,
          type: 'transaction',
          value: totalVoucher || 0,
          source: 'seller',
        })
      }
      let shippingIntegratedCourier = {}
      if (isIntegratedCourier) {
        const selectedCourierIntegrated = this.listCourierIntegrated[courierIntegrated]
        shippingIntegratedCourier = {
          use_shipping_api: true,
          destination_area_id: this.form.subdistrict,
          store_id: this.storeId,
          id: uuidv4(),
          cost_type: selectedCourierIntegrated?.cost_type,
          courier: selectedCourierIntegrated?.name,
          service_code: selectedCourierIntegrated?.service?.code,
          service_type: selectedCourierIntegrated?.service?.type,
          service_name: selectedCourierIntegrated?.service?.name,
          shipping_cost: {
            value: shippingCost || 0,
            currency: this.currency,
          },
          insurance_cost: {
            value: insuranceCost || 0,
            currency: this.currency,
          },
          remarks: null,
          shipping_label_url: finalLabelGenerated,
          awb: awbNumber,
        }
      }
      let isQtyHaveZeroValue = false
      let isUomHaveEmptyValue = false
      const payload = {
          customer_data: {
            id: uuidv4(),
            entity_name: `${firstName} ${lastName}`,
            first_name: firstName,
            last_name: lastName,
            email: email || '-',
            phone: phoneNumber ? extPhone + phoneNumber : '-',
          },
          billing_data: {
            id: uuidv4(),
            first_name: firstName,
            last_name: lastName,
            email: email || '-',
            phone: phoneNumber ? extPhone + phoneNumber : '-',
            address: payloadBillingAddress,
          },
          destination_data: {
            id: uuidv4(),
            first_name: firstName,
            last_name: lastName,
            email: email || '-',
            phone: phoneNumber ? extPhone + phoneNumber : '-',
            address: withCourier ? payloadShippingAddress : payloadBillingAddress,
          },
          shipping_data: isIntegratedCourier ? shippingIntegratedCourier : finalShippingMethod,
          promotions: {
            discounts: discountList,
            vouchers_used: voucherList,
          },
          payment: {
            term: 'CBD',
            method: paymentType,
            payment_name: selectedPayment?.payment_name,
            payment_type: selectedPayment?.payment_type,
            gross_amount: {
              currency: this.currency,
            },
          },
          warehouse_id: warehouseId,
          po_number: '',
          order_number: orderNumber?.toUpperCase(),
          attachment_url: attachmentUrl,
          created_at: this.$moment.utc(orderDate).format(this.$moment.HTML5_FMT.DATETIME_LOCAL_SECONDS), 
          order_item: this.itemList.map((obj) => {
            if (!Boolean(obj.quantity || 0)) {
              isQtyHaveZeroValue = true
            }
            if (!Boolean(obj.uom)) {
              isUomHaveEmptyValue = true
            }
            const selectedPrice = obj.price?.find((obj2) => obj2.uom === obj.uom)
            return {
              catalog_id: obj.catalog_id.split('.')[0],
              catalog_sku: obj.sku,
              title: obj.catalog_title,
              product_id: obj.catalog_id.split('.')[1],
              uom: obj.uom,
              vat: null,
              price_id: selectedPrice?.price_id,
              discount: {
                currency: this.currency,
                value: obj.product_discount || 0,
              },
              selling_price: {
                currency: this.currency,
                value: obj.selling_price,
              },
              image_url: obj.showimg_url,
              qty: obj.quantity || 0,
              status: 'OPEN',
            }
          }),
      }
      if (isQtyHaveZeroValue) {
        this.$notification.error({
          message: this.$t('manualOrder.fail_create_order'),
          description: this.$t('manualOrder.check_qty_warning'),
        })
        this.isLoading = false
        return
      }
      if (isUomHaveEmptyValue) {
        this.$notification.error({
          message: this.$t('manualOrder.fail_create_order'),
          description: this.$t('manualOrder.check_uom_warning'),
        })
        this.isLoading = false
        return
      }
      await createManualOrder({
        business_id: this.businessId,
        channel_id: params.id,
        data: payload,
      })
      .then(async ({ data }) => {
        if (this.fileList.length) {
          await addManualOrderAttachment({
            business_id: this.businessId,
            channel_code: this.$route.query.channel_code,
            channel_id: this.$route.params.id,
            order_id: data?.data?.id,
            order_number: data?.data?.order_number,
            files: this.fileList,
          })
          .catch(err => console.error(err))
          .finally(() => {
            this.$notification.success({
              message: this.$t('manualOrder.success_create_order'),
              description: `${this.$t('manualOrder.order_number_label')}: ${data?.data?.order_number}`,
            })
            this.$router.go(-1)
          })
        } else {
          this.$notification.success({
            message: this.$t('manualOrder.success_create_order'),
            description: `${this.$t('manualOrder.order_number_label')}: ${data?.data?.order_number}`,
          })
          this.$router.go(-1)
        }
      })
      .catch(err => {
        console.error(err)
        this.$notification.error({
          message: this.$t('manualOrder.fail_create_order'),
          description: err?.response?.data?.message || this.$t('manualOrder.error_occurred'),
        })
      })
      .finally(() => this.isLoading = false)
    }, 1000),
    async fetchInventory() {
      this.isLoading = true
      const { warehouseId } = this.form
      if (!warehouseId) {
        this.isLoading = false
        return
      }
      this.catalogPage = 1
      const bodyValue = {
        business_id: this.businessId,
        search: this.search,
        page: 1,
        limit: 20,
        warehouse_id: warehouseId,
        is_bundling_included: true,
      }
      this.isLoading = false
      await getCatalogListGR(bodyValue)
        .then(({ data: response }) => {
          this.inventories = response.data || []
          this.catalogTotalRow = response.total_row || 0
        })
        .catch(err => {
          this.inventories = []
          this.catalogTotalRow = 0
          console.error(err)
        })
        .finally(() => this.isLoading = false)
    },
    async fetchInventoryNextPage() {
      const { warehouseId } = this.form
      if (!warehouseId || this.catalogFetching) return
      if (this.inventories.length >= this.catalogTotalRow) return
      this.catalogFetching = true
      const nextPage = this.catalogPage + 1
      const bodyValue = {
        business_id: this.businessId,
        search: this.search,
        page: nextPage,
        limit: 20,
        warehouse_id: warehouseId,
        is_bundling_included: true,
      }
      await getCatalogListGR(bodyValue)
        .then(({ data: response }) => {
          const newItems = response.data || []
          this.inventories = [...this.inventories, ...newItems]
          this.catalogTotalRow = response.total_row || this.catalogTotalRow
          this.catalogPage = nextPage
        })
        .catch(err => {
          console.error(err)
        })
        .finally(() => {
          this.catalogFetching = false
        })
    },
    handleCatalogPopupScroll(e) {
      const { target } = e
      const scrollBottom = target.scrollTop + target.offsetHeight
      if (scrollBottom >= target.scrollHeight - 20) {
        this.fetchInventoryNextPage()
      }
    },
    handleSearch: debounce(function(e) {
      this.search = e
    }, 500),
    handleResetSearch() {
      this.search = ''
    },
    async getProductUOM(value, record) {
      record.quantity = ''
      record.selling_price = ''
      record.product_discount = ''
      record.total_price = 0
      const findCatalog = this.inventories.find(item => {
        return item.id === value.split('.')[0]
      })
      record.catalog_title = findCatalog?.title || ''
      record.sku = findCatalog?.sku || ''
      record.showimg_url = findCatalog?.showimg_url || ''
      record.availability = findCatalog?.availability || 0
      this.fetching = true
      
      const uomPromise = getUom({
        id: value.split('.')[1],
        params: { business_id: this.businessId },
      }).then(({ data: { data: response } }) => {
        let levels = [];
        response.units.forEach((item, index) => {
          levels.push(item.unit);
          record.units[item.unit] = {
            numerator: item.numerator,
            denumerator: item.denumerator,
          };
          if (index === 0) record.uom = item.unit;
          if (item.numerator === 1 && item.denumerator === 1) record.base_uom = item.unit;
        });
        record.levels = levels;
      }).catch(() => {
        record.uom = '';
        record.base_uom = '';
        record.levels = [];
        record.units = {};
        record.availability = 0;
      });

      const priceRangePromise = getPriceRange({
        business_id: this.businessId,
        product_id: value.split('.')[1],
      }).then(({ data: { data: response } }) => {
        const tempPrice = response.find((obj) => obj.catalog_id === value.split('.')[0])?.price;
        const msrp = tempPrice?.find((obj) => obj.uom === record.uom)?.value;
        record.price = tempPrice;
        record.msrp = msrp;
        record.selling_price = msrp;
      }).catch(() => {
        record.price = null;
      });

      const weightPromise = getShippingUnit({
        product_id: value.split('.')[1],
        business_id: this.businessId,
      }).then(({ data: { data: response } }) => {
        if (response.product_units?.length > 0) {
          record.product_units = response.product_units
        } else {
          record.product_units = []
        }
      }).catch((err) => {
        console.error(err)
        record.product_units = []
      });

      Promise.all([uomPromise, priceRangePromise, weightPromise])
        .finally(() => {
          this.fetching = false;
          this.$forceUpdate();
        });
    },
    onChangeQuantity(event, record) {
      record.base_quantity = +event * +record.units[record.uom].numerator
    },
    onDeleteItem(record) {
      const filter = this.itemList.filter(v => {return v.item_reference_id !== record.item_reference_id})
      const temp = filter.map((v, i) => {
        const data = {
          ...v,
          item_reference_id: typeof v.item_reference_id === 'string' ? v.item_reference_id : i + 1,
        }
        return data
      })
      this.itemList = temp
    },
    addItem() {
      if (this.itemList.length >= this.defaultMaxItem) {
        this.$notification.error({
          description: this.$t('manualOrder.max_item_warning', { max: this.defaultMaxItem }),
          message: this.$t('manualOrder.error_add_item'),
        })
        return
      } 
      const arr = this.itemList
      const temp = {
        item_reference_id: arr.length + 1,
        catalog_id: '',
        catalog_title: '',
        quantity: '',
        uom: '',
        sku: '',
        batch_number: '',
        expired_date: null,
        stock_type: 'AV',
        base_quantity: '',
        base_uom: '',
        serial_number: '',
        levels: [],
        units: {},
        price: {},
        msrp: null,
        total_price: null,
        selling_price: '',
        product_discount: null,
        currency: this.currency,
     }
     arr.push(temp)
     this.itemList = arr
    },
    checkOrderNumber(event) {
      this.form.orderNumber = event.target.value?.toUpperCase()
    },
    flagComponent(param) {
      if (param === '62' || param === 'Indonesia') return IndonesiaFlag
      if (param === '60') return MalaysiaFlag
      if (param === '65') return SingaporeFlag
      if (param === '27') return SouthAfricaFlag
    },
    handleRemove(file) {
      const index = this.fileList.indexOf(file)
      const newFileList = this.fileList.slice()
      newFileList.splice(index, 1)
      this.fileList = newFileList
    },
    beforeUpload(file) {
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
      if (!isPdf) {
        this.$message.error('Hanya menerima file PDF')
        return false
      }

      const isLt4MB = file.size <= 4 * 1024 * 1024
      if (!isLt4MB) {
        this.$message.error('File must be smaller than 4MB')
        return false
      }

      if (this.fileList.length > 2) {
        this.$message.error('Maksimal hanya 3 file')
        return false
      }

      this.fileList = [...this.fileList, file]
      return false
    },
    handleCancelOrder() {
      this.$confirm({
        title: this.$t('manualOrder.cancel_confirm_title') || 'Batalkan Pembuatan Pesanan?',
        content: this.$t('manualOrder.cancel_confirm_content') || 'Semua data yang telah dimasukkan belum disimpan dan akan hilang.',
        okText: this.$t('manualOrder.yes_cancel') || 'Ya, Batalkan',
        okType: 'danger',
        cancelText: this.$t('manualOrder.back') || 'Kembali',
        onOk: () => {
          this.goBack()
        },
      })
    },
    goBack() {
      this.$router.back({
        path: '/inventory/goods-receipt',
        query: { ...this.$route.query },
      })
    },
  },
}
</script>

<style lang="scss">
@import './create.module.scss';
.group-product-select .ant-select-dropdown-menu-item-group-title {
  height: fit-content !important;
  line-height: normal !important;
  border-top: 1px solid rgba(0, 0, 0, 0.45);
  padding-top: 10px;
}

.select-input {
  input:focus {
    border: 1px solid #4b7cf3;
  }
  .ant-input {
    border: 1px solid #e4e9f0;
    border-left: none;
  }
  .ant-select-selection.ant-select-selection--single {
    border-bottom-right-radius: 0;
    background-color: #ffffff;
    border: 1px solid #e4e9f0;
    border-right: none;
    width: 100px;
    margin-top: -36px !important;
  }
  .ant-select-selection-selected-value {
    margin-top: 6px;
    display: flex !important;
  }
  form .ant-input-group .ant-select {
    height: 0px;
  }
  .ant-input-group-addon {
    background-color: transparent !important;
  }
  .ant-input-group-addon:first-child .ant-select .ant-select-selection {
    margin-top: 5px !important;
  }
  .ant-select-selection__rendered {
    line-height: 21px !important;
  }
}
.ant-upload-list-picture {
  .ant-upload-list-item {
    height: 40px !important;
  }
  .ant-upload-list-item-thumbnail {
    width: 24px !important;
    height: 24px !important;
    top: 0 !important;
    font-size: 24px !important;
    line-height: 46px !important;
  }
  .ant-upload-list-item-name {
    line-height: 22px !important;
  }
}
.ant-upload-list-item-card-actions.picture {
  top: 12px !important;
}
</style>
<style lang="scss" scoped>
::v-deep .ant-card {
  margin-top: 12px !important;
}
::v-deep .ant-card-head {
  min-height: 38px !important;
  padding: 0 16px !important;
  border-bottom: 1px solid #f0f0f0 !important;
  .ant-card-head-title {
    padding: 8px 0 !important;
    font-size: 13px !important;
    font-weight: 600;
  }
}
::v-deep .ant-card-body {
  padding: 12px 16px !important;
}
::v-deep .ant-form-item {
  margin-bottom: 8px !important;
  .ant-form-item-label {
    line-height: 1.3 !important;
    padding-bottom: 2px !important;
    label {
      font-size: 11px !important;
      font-weight: 500;
      color: #64748b;
    }
  }
}
.ant-select-lg .ant-select-selection--single .product-select {
  white-space: nowrap !important;
}
.product-select {
  white-space: pre-wrap !important;
  .sku-desc {
    font-size: 12px;
    font-weight: 500;
  }
}

.ant-input-number-handler-wrap .remove-arrow {
  width: 0px !important;
}

.addon-before {
  width: 75px;
  height: 40px;
  padding: 8px 0;
  font-size: 15px;
  background-color: #fafafa;
  border: 1px solid #d9d9d9;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  text-align: center;
}

.sticky-col {
  position: sticky !important;
  top: 85px !important;
  align-self: flex-start !important;
  z-index: 8;
}

.sticky-sidebar {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 105px);
}

.sidebar-scroll-area {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #e2e8f0 transparent;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
}

.sidebar-sticky-actions {
  padding: 12px 0 4px;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.mini-cart-container {
  max-height: 280px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;

  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
}

.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.summary-label {
  font-size: 13px;
  flex: 1;
  min-width: 0;
}

.summary-input-wrap {
  width: 175px;
  flex-shrink: 0;
}

.summary-input-group {
  display: flex !important;
  width: 100%;

  .input-addon-currency {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px;
    background-color: #fafafa;
    border: 1px solid #d9d9d9;
    border-right: none;
    border-radius: 4px 0 0 4px;
    font-size: 11px;
    font-weight: 700;
    color: #475569;
    height: 32px;
    line-height: 30px;
    box-sizing: border-box;
  }

  ::v-deep .ant-input-number {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    height: 32px !important;

    .ant-input-number-input {
      height: 30px !important;
      line-height: 30px !important;
    }
  }
}

.product-title {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  font-size: 13px;
  line-height: 1.3;
}

.product-sku {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
}

.mini-off-badge {
  display: inline-block;
  background: #fee2e2;
  color: #e11d48;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  border-radius: 3px;
  padding: 1px 4px;
  text-transform: uppercase;
}

.extra-small {
  font-size: 11px;
}
</style>